// BatchesPanel — purchase batching ("Закупка"), migration 0076.
//
// One component, two surfaces, selected by `scope`:
//   scope="self"        → reseller groups their own self-purchase orders.
//   scope="facilitated" → operator groups facilitated orders to buy.
//
// Lists batches, lets you create one, edit retailer / supplier ref /
// delivery tariff / status, add & remove member orders, set per-order
// weight, and see the goods + delivery (weight × tariff) totals.
import { useState, useEffect, useCallback } from "react";
import { api } from "../api.js";
import { T, T_NEW } from "../theme.js";
import { Btn, Mono, Serif } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT } from "../i18n.jsx";

const BATCH_STATUSES = ["open", "ordered", "shipped", "closed", "cancelled"];
const rub = (cents) => `₽${(cents / 100).toFixed(0)}`;

export function BatchesPanel({ scope = "self" }) {
  const t = useT();
  const toast = useToast();
  const [batches, setBatches] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [creating, setCreating] = useState(false);

  const loadCandidates = useCallback(async () => {
    try {
      const all = scope === "self"
        ? await api.getMyOrders()
        : await api.getAdminOrders({ limit: 500 });
      setCandidates((all || []).filter((o) =>
        !o.purchaseBatchId &&
        (scope === "self" ? o.facilitated === false : o.facilitated === true) &&
        o.status !== "cancelled" && o.status !== "unavailable"));
    } catch { /* non-fatal */ }
  }, [scope]);

  const reload = useCallback(async () => {
    try {
      setBatches(await api.listBatches(scope));
    } catch (e) { toast.error(e.message); setBatches([]); }
    loadCandidates();
  }, [scope, toast, loadCandidates]);

  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    setCreating(true);
    try { await api.createBatch({ scope, retailer: "" }); await reload(); }
    catch (e) { toast.error(e.message); }
    finally { setCreating(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <Serif size={T_NEW.displayMd} style={{ flex: 1, fontWeight: 500 }}>
          {t("batch.heading")}
        </Serif>
        <Btn disabled={creating} onClick={create}>{t("batch.new")}</Btn>
      </div>

      {batches === null ? (
        <div style={{ padding: 28, textAlign: "center", color: T_NEW.ink56, fontFamily: T_NEW.fontSans, fontSize: T_NEW.meta }}>
          {t("common.loading")}
        </div>
      ) : batches.length === 0 ? (
        <div style={{ padding: 28, textAlign: "center", color: T_NEW.ink56, fontFamily: T_NEW.fontSerif,
                      fontStyle: "italic", fontSize: T_NEW.bodySm, lineHeight: 1.55 }}>
          {t("batch.empty")}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {batches.map((b) => (
            <BatchCard key={b.id} batch={b} scope={scope} candidates={candidates}
                       onChanged={reload} t={t} toast={toast} />
          ))}
        </div>
      )}
    </div>
  );
}

function BatchCard({ batch, scope, candidates, onChanged, t, toast }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);     // { ...batch, members }
  const [adding, setAdding] = useState(false);
  const [pick, setPick] = useState(new Set());

  const loadDetail = useCallback(async () => {
    try { setDetail(await api.getBatch(batch.id)); }
    catch (e) { toast.error(e.message); }
  }, [batch.id, toast]);

  useEffect(() => { if (open && !detail) loadDetail(); }, [open, detail, loadDetail]);

  const b = detail || batch;
  const rate = b.deliveryRateRubPerKg || 0;
  const members = detail?.members || [];
  const goodsCents = members.reduce((s, m) => s + (m.rubCents || 0), 0);
  const deliveryCents = members.reduce((s, m) => s + Math.round((m.weightKg || 0) * rate * 100), 0);

  const patch = async (p) => {
    try { setDetail(await api.updateBatch(batch.id, p)); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  const setWeight = async (orderId, w) => {
    try {
      await api.setOrderWeight(orderId, w);
      await loadDetail();
    } catch (e) { toast.error(e.message); }
  };
  const removeMember = async (orderId) => {
    try { const r = await api.removeBatchOrder(batch.id, orderId); setDetail((d) => ({ ...d, members: r.members })); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  const addPicked = async () => {
    try {
      const r = await api.addBatchOrders(batch.id, [...pick]);
      setDetail((d) => ({ ...d, members: r.members }));
      setPick(new Set()); setAdding(false); onChanged();
    } catch (e) { toast.error(e.message); }
  };
  const del = async () => {
    if (!await toast.confirm(t("batch.delete.confirm"))) return;
    try { await api.deleteBatch(batch.id); onChanged(); }
    catch (e) { toast.error(e.message); }
  };

  const inp = {
    background: T.bg, color: T.text, border: `1px solid ${T.border}`,
    borderRadius: 8, padding: "6px 8px", fontSize: 13, fontFamily: "inherit",
  };

  return (
    <div style={{ border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r2, overflow: "hidden", background: T_NEW.paper }}>
      {/* Header */}
      <div onClick={() => setOpen((x) => !x)}
           style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Serif size={T_NEW.bodyLg} style={{ display: "block" }}>
            {b.retailer || t("batch.unnamed")}
          </Serif>
          <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginTop: 2 }}>
            {t("batch.itemsN", { n: b.memberCount ?? members.length })}
            {b.supplierOrderRef ? ` · ${b.supplierOrderRef}` : ""}
          </Mono>
        </div>
        <span style={{ fontSize: T_NEW.micro, fontFamily: T_NEW.fontMono, fontWeight: 700,
                       textTransform: "uppercase", letterSpacing: 0.6,
                       color: b.status === "cancelled" ? T_NEW.danger
                            : b.status === "closed" ? T_NEW.bottle : T_NEW.persimmon }}>
          {t(`batch.status.${b.status}`)}
        </span>
        <Mono size={T_NEW.meta} color={T_NEW.ink} style={{ fontWeight: 700 }}>
          {rub(b.membersRubCents ?? goodsCents)}
        </Mono>
        <span style={{ width: 14, textAlign: "center", color: T_NEW.ink56, fontFamily: T_NEW.fontMono }}>
          {open ? "−" : "+"}
        </span>
      </div>

      {open && (
        <div style={{ borderTop: `1px solid ${T_NEW.ink12}`, padding: "12px 16px" }}>
          {/* Editable fields */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <Field label={t("batch.retailer")}>
              <input defaultValue={b.retailer} onBlur={(e) => e.target.value !== b.retailer && patch({ retailer: e.target.value })}
                     placeholder={t("batch.retailer.ph")} style={{ ...inp, width: "100%" }} />
            </Field>
            <Field label={t("batch.supplierRef")}>
              <input defaultValue={b.supplierOrderRef || ""} onBlur={(e) => patch({ supplierOrderRef: e.target.value })}
                     placeholder={t("batch.supplierRef.ph")} style={{ ...inp, width: "100%" }} />
            </Field>
            <Field label={t("batch.tariff")}>
              <input type="number" min="0" step="1" defaultValue={rate}
                     onBlur={(e) => Number(e.target.value) !== rate && patch({ deliveryRateRubPerKg: Number(e.target.value) || 0 })}
                     style={{ ...inp, width: "100%", fontFamily: T_NEW.fontMono }} />
            </Field>
            <Field label={t("batch.statusLabel")}>
              <select value={b.status} onChange={(e) => patch({ status: e.target.value })}
                      style={{ ...inp, width: "100%", cursor: "pointer" }}>
                {BATCH_STATUSES.map((s) => <option key={s} value={s}>{t(`batch.status.${s}`)}</option>)}
              </select>
            </Field>
          </div>

          {/* Members */}
          {members.length === 0 ? (
            <div style={{ padding: "12px 0", color: T_NEW.ink56, fontStyle: "italic",
                          fontFamily: T_NEW.fontSerif, fontSize: T_NEW.meta }}>
              {t("batch.noMembers")}
            </div>
          ) : (
            <div style={{ marginBottom: 10 }}>
              {members.map((m) => (
                <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0",
                                         borderBottom: `1px solid ${T_NEW.ink12}` }}>
                  {m.post?.imageUrl && (
                    <img src={m.post.imageUrl} alt="" style={{ width: 40, height: 40, objectFit: "cover",
                                                               borderRadius: T_NEW.r1, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontFamily: T_NEW.fontSerif, fontSize: T_NEW.body,
                                   overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.post?.title || "—"}
                    </span>
                    <span style={{ fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro, color: T_NEW.ink56 }}>
                      {m.variant ? `${m.variant} · ` : ""}×{m.qty} · {rub(m.rubCents)}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <input type="number" min="0" step="0.1" defaultValue={m.weightKg ?? ""}
                           placeholder={t("batch.kg")}
                           onBlur={(e) => {
                             const v = e.target.value.trim() === "" ? null : Number(e.target.value);
                             if (v !== (m.weightKg ?? null)) setWeight(m.id, v);
                           }}
                           style={{ ...inp, width: 56, textAlign: "center", fontFamily: T_NEW.fontMono }} />
                    <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56 }}>{t("batch.kg")}</span>
                  </div>
                  <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ width: 64, textAlign: "right" }}>
                    {m.weightKg ? rub(Math.round(m.weightKg * rate * 100)) : "—"}
                  </Mono>
                  <button onClick={() => removeMember(m.id)}
                          style={{ background: "none", border: "none", color: T_NEW.danger, cursor: "pointer",
                                   fontSize: 16, lineHeight: 1, padding: "0 2px" }}>×</button>
                </div>
              ))}
            </div>
          )}

          {/* Totals */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 18, marginBottom: 12,
                        fontFamily: T_NEW.fontMono, fontSize: T_NEW.meta }}>
            <span style={{ color: T_NEW.ink56 }}>{t("batch.goods")}: <b style={{ color: T_NEW.ink }}>{rub(goodsCents)}</b></span>
            <span style={{ color: T_NEW.ink56 }}>{t("batch.delivery")}: <b style={{ color: T_NEW.ink }}>{rub(deliveryCents)}</b></span>
            <span style={{ color: T_NEW.ink56 }}>{t("batch.grand")}: <b style={{ color: T_NEW.persimmon }}>{rub(goodsCents + deliveryCents)}</b></span>
          </div>

          {/* Add orders */}
          {adding ? (
            <div style={{ background: T_NEW.paperSoft, borderRadius: T_NEW.r1, padding: 10, marginBottom: 10 }}>
              {candidates.length === 0 ? (
                <div style={{ color: T_NEW.ink56, fontStyle: "italic", fontFamily: T_NEW.fontSerif, fontSize: T_NEW.meta }}>
                  {t("batch.noCandidates")}
                </div>
              ) : candidates.map((o) => (
                <label key={o.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", cursor: "pointer" }}>
                  <input type="checkbox" checked={pick.has(o.id)}
                         onChange={() => setPick((s) => { const n = new Set(s); n.has(o.id) ? n.delete(o.id) : n.add(o.id); return n; })}
                         style={{ accentColor: T_NEW.persimmon }} />
                  <span style={{ flex: 1, fontSize: T_NEW.meta, color: T_NEW.ink, overflow: "hidden",
                                 textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {o.post?.title || "—"} {o.variant ? `· ${o.variant}` : ""}
                  </span>
                  <span style={{ fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro, color: T_NEW.ink56 }}>
                    {o.orderNumber || ""}
                  </span>
                </label>
              ))}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                <Btn variant="ghost" onClick={() => { setAdding(false); setPick(new Set()); }}>{t("common.cancel")}</Btn>
                <Btn disabled={pick.size === 0} onClick={addPicked}>{t("batch.addSelected", { n: pick.size })}</Btn>
              </div>
            </div>
          ) : (
            <Btn variant="outline" onClick={() => setAdding(true)}>{t("batch.addOrders")}</Btn>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button onClick={del}
                    style={{ background: "none", border: "none", color: T_NEW.danger, cursor: "pointer",
                             fontSize: T_NEW.micro, fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                             letterSpacing: 0.6, fontWeight: 700 }}>
              {t("batch.delete")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: T_NEW.ink56, marginBottom: 4, fontWeight: 600,
                    textTransform: "uppercase", letterSpacing: 0.5, fontFamily: T_NEW.fontMono }}>
        {label}
      </div>
      {children}
    </div>
  );
}
